/**
 * 📱 App Installs by Source — admin dashboard card.
 *
 * Phase 10.1 placeholder. Shows weekly install counts grouped by `ref`
 * (home, magazine, artist, …). Renders a clean empty state until the
 * Play Store URL is live and Install Referrer events start flowing.
 */
import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const REF_LABELS = {
  home: "🏠 Home",
  magazine: "📖 Magazine",
  artist: "🎤 Artist",
  track: "🎶 Track",
  charts: "📈 Charts",
  explore: "🔎 Explore",
  live: "🎙️ Live",
  community: "🤝 Community",
  spotlight: "⭐ Spotlight",
  hub: "💼 Hub",
  support: "🎧 Support",
  auth: "🔐 Auth",
  other: "❓ Other",
};

const AppInstallsBySourceCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/admin/app-installs/by-ref?days=7");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load install attribution");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const total = data?.total ?? 0;
  const sources = data?.sources || [];
  const max = sources.reduce((m, s) => Math.max(m, s.count || 0), 0) || 1;

  return (
    <Card
      className="p-6"
      style={{ background: "rgba(34, 24, 50, 0.35)", borderColor: "rgba(168, 85, 247, 0.25)" }}
      data-testid="app-installs-by-ref-card"
    >
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <span>📱</span> App Installs by Source
          </h3>
          <p className="text-sm text-gray-400 mt-1 max-w-2xl">
            Last 7 days, grouped by the web surface that drove the install
            (Play Install Referrer). Rolls in once the Play Store link goes live.
          </p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          className="text-white border-gray-700"
          data-testid="app-installs-refresh"
        >
          {loading ? "Loading…" : "Refresh"}
        </Button>
      </div>

      {error && (
        <div
          className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-200 text-sm"
          data-testid="app-installs-error"
        >
          {error}
        </div>
      )}

      {!error && total === 0 && (
        <div
          className="p-6 rounded-xl border border-purple-500/20 bg-black/30 text-center"
          data-testid="app-installs-empty"
        >
          <div className="text-4xl mb-2">📊</div>
          <p className="text-white font-bold text-sm">No install data yet</p>
          <p className="text-gray-400 text-xs mt-1 max-w-md mx-auto leading-5">
            Install attribution will appear here after the Play Store link goes live.
          </p>
        </div>
      )}

      {!error && total > 0 && (
        <div className="space-y-2" data-testid="app-installs-list">
          <div className="flex items-baseline justify-between text-xs text-gray-400 mb-2">
            <span>
              <span className="text-amber-300 font-bold">{total}</span> install
              {total === 1 ? "" : "s"} in the last {data?.days ?? 7} days
            </span>
            {(data?.total_all_time ?? 0) > 0 && (
              <span className="text-gray-500">All-time: {data.total_all_time}</span>
            )}
          </div>
          {sources
            .slice()
            .sort((a, b) => (b.count || 0) - (a.count || 0))
            .map((s) => {
              const pct = total > 0 ? Math.round(((s.count || 0) / total) * 100) : 0;
              const barW = Math.round(((s.count || 0) / max) * 100);
              return (
                <div
                  key={s.ref}
                  className="grid grid-cols-[120px_1fr_64px] items-center gap-3"
                  data-testid={`app-installs-row-${s.ref}`}
                >
                  <span className="text-xs text-gray-300 font-bold">
                    {REF_LABELS[s.ref] || s.ref}
                  </span>
                  <div className="h-2.5 rounded-full bg-purple-900/30 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
                      style={{ width: `${barW}%` }}
                    />
                  </div>
                  <span className="text-right text-xs text-gray-300">
                    <span className="text-white font-bold">{s.count}</span>
                    <span className="text-gray-500"> · {pct}%</span>
                  </span>
                </div>
              );
            })}
        </div>
      )}
    </Card>
  );
};

export default AppInstallsBySourceCard;
